import React, { useEffect, useState } from "react";
import {
  Box, Typography, Alert, Drawer, MenuItem, Chip, alpha,
} from "@mui/material";
import {
  ManageAccounts as ManageAccountsIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import { useClinicTheme } from "../../theme";
import { DrawerField, tileInputProps } from "../../components/molecules";
import {
  DrawerHeader, DrawerFooter, FormSelect, FormTextField, NameInput, PhoneInput,
} from "../../components/atoms";

import { COLORS, TYPO } from "../../styles/uiConstants";

const ROLE_TPL_STORAGE_KEY = "pupill:adminRoleTemplates";
function loadStoredTemplates() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(ROLE_TPL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch (_e) { return []; }
}

export default function CreateAdminStaffDrawer({
  open, onClose, form, setField, onSubmit, isLoading, formError,
}) {
  const { palette } = useClinicTheme();
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (open) setTemplates(loadStoredTemplates());
  }, [open]);

  const selectedTpl = templates.find((t) => t._id === form.roleTemplateId);

  const handleTemplateChange = (id) => {
    setField("roleTemplateId", id);
    const tpl = templates.find((t) => t._id === id);
    setField("permissions", tpl?.permissions || []);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<ManageAccountsIcon />}
        title="Personel Oluştur"
        subtitle="Yeni personel hesabı oluşturun"
        onClose={onClose}
      />

      {/* Body — InfoTile grid */}
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: COLORS.white, flex: 1, minHeight: 0, overflowY: "auto" }}>
        {formError && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2, fontWeight: 600 }}>
            {formError}
          </Alert>
        )}
        <Box sx={{ display: "grid", rowGap: 2.5, columnGap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>

          {/* Rol Seç — full width, en üstte */}
          <DrawerField label="Rol Seç" fullWidth>
            <FormSelect
              variant="standard"
              value={form.roleTemplateId || ""}
              onChange={(v) => handleTemplateChange(v)}
              placeholder={<em>Seçilmedi</em>}
              InputProps={{ disableUnderline: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (val) => {
                  if (!val) return "Seçilmedi (boş izinle oluştur)";
                  const t = templates.find((x) => x._id === val);
                  return t ? `${t.name} · ${(t.permissions || []).length} izin` : val;
                },
              }}
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: TYPO.body,
                  fontWeight: 600,
                  color: form.roleTemplateId ? COLORS.slate800 : COLORS.slate400,
                },
                "& .MuiSelect-select": { py: 0 },
              }}
            >
              {templates.map((t) => (
                <MenuItem key={t._id} value={t._id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <ShieldIcon sx={{ fontSize: 16, color: palette.primary }} />
                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: TYPO.body }}>{t.name}</Typography>
                    <Chip
                      label={`${(t.permissions || []).length} izin`}
                      size="small"
                      sx={{ bgcolor: alpha(palette.primary, 0.1), color: palette.primary, fontWeight: 700, fontSize: "0.7rem" }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </FormSelect>
          </DrawerField>

          {/* Ad / Soyad */}
          <DrawerField label="Ad">
            <NameInput
              value={form.name}
              onChange={(v) => setField("name", v)}
              fullWidth size="small" variant="standard"
              required
              InputProps={tileInputProps(true)}
            />
          </DrawerField>
          <DrawerField label="Soyad">
            <NameInput
              value={form.surname}
              onChange={(v) => setField("surname", v)}
              fullWidth size="small" variant="standard"
              required
              InputProps={tileInputProps(true)}
            />
          </DrawerField>

          {/* Email — full width */}
          <DrawerField label="E-posta" fullWidth>
            <FormTextField
              type="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              fullWidth size="small" variant="standard"
              required
              InputProps={tileInputProps(true)}
            />
          </DrawerField>

          {/* Şifre / Şifre Tekrar */}
          <DrawerField label="Şifre">
            <FormTextField
              type="password"
              value={form.password}
              onChange={(e) => setField("password", e.target.value)}
              fullWidth size="small" variant="standard"
              required
              InputProps={tileInputProps(true)}
            />
          </DrawerField>
          <DrawerField label="Şifre Tekrar">
            <FormTextField
              type="password"
              value={form.confirmPassword}
              onChange={(e) => setField("confirmPassword", e.target.value)}
              fullWidth size="small" variant="standard"
              required
              InputProps={tileInputProps(true)}
            />
          </DrawerField>

          {/* Telefon */}
          <DrawerField label="Telefon">
            <PhoneInput
              value={form.tellNo}
              onChange={(v) => setField("tellNo", v)}
              fullWidth size="small" variant="standard"
              required
              InputProps={tileInputProps(true)}
            />
          </DrawerField>

          {/* TC */}
          <DrawerField label="TC Kimlik No">
            <FormTextField
              value={form.tcNo}
              onChange={(e) => setField("tcNo", (e.target.value || "").replace(/\D/g, "").slice(0, 11))}
              fullWidth size="small" variant="standard"
              required
              inputProps={{ maxLength: 11, inputMode: "numeric" }}
              InputProps={tileInputProps(true)}
            />
          </DrawerField>
        </Box>

        {templates.length === 0 && (
          <Alert severity="warning" sx={{ borderRadius: 2, mt: 2 }}>
            Henüz personel şablonu yok. Önce <strong>Kullanıcı Yönetimi → Personel Şablonları</strong> sayfasından şablon oluşturabilirsiniz.
          </Alert>
        )}

        {selectedTpl && (
          <Box sx={{
            mt: 2, p: 1.75, borderRadius: 2,
            bgcolor: alpha(palette.primary, 0.06),
            border: `1px solid ${alpha(palette.primary, 0.22)}`,
            display: "flex", alignItems: "center", gap: 1.25,
          }}>
            <ShieldIcon sx={{ color: palette.primary, fontSize: 18 }} />
            <Typography sx={{ color: COLORS.slate700, fontWeight: 500, flex: 1, fontSize: TYPO.body }}>
              <strong>{selectedTpl.name}</strong> şablonundan {(selectedTpl.permissions || []).length} izin uygulanacak.
            </Typography>
          </Box>
        )}

        <Alert severity="info" sx={{ borderRadius: 2, mt: 1.5 }}>
          <Typography sx={{ fontWeight: 500, fontSize: TYPO.body }}>
            Oluşturulan personel otomatik olarak <strong>"Aktif"</strong> durumda olacak.
          </Typography>
        </Alert>
      </Box>

      <DrawerFooter
        onCancel={onClose}
        onSubmit={onSubmit}
        submitLabel="Personel Oluştur"
        loadingLabel="Oluşturuluyor..."
        loading={isLoading}
        startIcon={<ManageAccountsIcon sx={{ fontSize: 15 }} />}
        fullWidth
      />
    </Drawer>
  );
}
